/**
 * The vendor register: for each supplier, where its invoices come from.
 *
 * Scanned and operated, not read. The craft goes on making the list legible
 * cold and on the two things somebody actually comes here to do: fill in a
 * portal that is still blank, and write a rule without breaking an existing
 * one.
 *
 * Shape follows what the suppliers screens in Xero, QuickBooks and Ramp all
 * converged on: a list of vendors with the identifying reference on the row,
 * and editing on the row rather than behind a navigation. Rejected their
 * per-vendor detail page, because there are twenty-three of these and nothing
 * on a profile needs a page of its own.
 *
 * The rule editor shows what a rule would claim BEFORE it is saved, which is
 * the part no supplier screen does, because none of them match on a regex.
 * Writing one blind is how one profile quietly starts taking another's charges.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  Check,
  ExternalLink,
  Loader2,
  KeyRound,
  Pencil,
  Plus,
  Search,
  Store,
  Trash2,
  X,
} from "lucide-react";

import { apiService } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const DOCUMENT_KINDS = [
  { value: "invoice", label: "Invoice" },
  { value: "invoice_plus_receipt", label: "Invoice and a separate receipt" },
  { value: "monthly_statement", label: "One monthly invoice, several charges" },
  { value: "none", label: "No document exists" },
];

const CADENCES = [
  { value: "per_transaction", label: "One per charge" },
  { value: "monthly", label: "Monthly" },
  { value: "annual", label: "Annual" },
];

const MATCH_ON = [
  { value: "eur_amount", label: "The euro amount" },
  { value: "foreign_amount", label: "The original currency amount" },
  { value: "reference", label: "The reference" },
];

const EMPTY_PROFILE = {
  name: "",
  match_patterns: [],
  match_card_last_four: [],
  match_category_ids: [],
  match_amount_min: null,
  match_amount_max: null,
  portal_url: "",
  portal_hint: "",
  account_ref: "",
  document_kind: "invoice",
  cadence: "per_transaction",
  charge_offset_days: 0,
  match_on: "eur_amount",
  no_document_reason: "",
  notes: "",
};

/**
 * A value you can change where you are reading it.
 *
 * Read and edit share their type and alignment so the row does not jump on
 * click, the tick and cross are visible rather than relying on blur, Return
 * saves and Escape discards, and the empty state carries the placeholder that
 * tells you the field is editable at all.
 */
const InlineText = ({ value, placeholder, label, mono, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setDraft(value || "");
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const commit = async () => {
    if (draft === (value || "")) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(draft);
      setEditing(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={`Edit ${label}`}
        onClick={() => setEditing(true)}
        className={`group inline-flex items-center gap-1.5 text-left text-sm max-w-full ${
          value ? "text-slate-700" : "text-slate-400 italic"
        }`}
      >
        {/* Truncate a value, never the placeholder. "Add a portal" clipped to
            "Add a porta" stops being an instruction and starts looking broken. */}
        <span
          className={value ? `truncate ${mono ? "font-mono text-xs" : ""}` : "whitespace-nowrap"}
          title={value || undefined}
        >
          {value || placeholder}
        </span>
        <Pencil className="w-3 h-3 flex-shrink-0 opacity-0 group-hover:opacity-100 text-slate-400" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        ref={inputRef}
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setDraft(value || "");
            setEditing(false);
          }
        }}
        className={`h-7 text-sm ${mono ? "font-mono text-xs" : ""}`}
        placeholder={placeholder}
      />
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 flex-shrink-0"
        aria-label="Save"
        disabled={saving}
        onClick={commit}
      >
        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
      </Button>
      <Button
        size="icon"
        variant="ghost"
        className="h-7 w-7 flex-shrink-0"
        aria-label="Cancel"
        disabled={saving}
        onClick={() => {
          setDraft(value || "");
          setEditing(false);
        }}
      >
        <X className="w-3 h-3" />
      </Button>
    </div>
  );
};

/** What a rule would claim, counted against the real transactions. */
const RulePreview = ({ criteria, profileId }) => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const key = JSON.stringify(criteria);
  useEffect(() => {
    const hasCriterion =
      criteria.match_patterns.length ||
      criteria.match_card_last_four.length ||
      criteria.match_category_ids.length ||
      criteria.match_amount_min != null ||
      criteria.match_amount_max != null;
    if (!hasCriterion) {
      setResult(null);
      setError(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    // Debounced: this counts every transaction, and it is being driven by
    // somebody typing a regex one character at a time.
    const timer = setTimeout(async () => {
      try {
        const res = await apiService.previewVendorProfile({ ...criteria, profile_id: profileId });
        if (!cancelled) {
          setResult(res.data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setResult(null);
          setError(err?.response?.data?.detail || "Could not check this rule");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, profileId]);

  if (error) {
    return (
      <p className="text-xs text-red-600 flex items-start gap-1.5">
        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
        {typeof error === "string" ? error : "Could not check this rule"}
      </p>
    );
  }

  if (!result) {
    return (
      <p className="text-xs text-slate-400">
        {loading ? "Checking..." : "Add a criterion to see what it would match."}
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-slate-600 tabular-nums">
        Matches <strong>{result.matches}</strong>{" "}
        {result.matches === 1 ? "transaction" : "transactions"}, of which{" "}
        <strong>{result.claimed}</strong> would land here.
      </p>
      {result.taken_by.length > 0 && (
        <p className="text-xs text-amber-700 flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>
            The rest already belong to{" "}
            {result.taken_by.map((t) => `${t.name} (${t.count})`).join(", ")}. A narrower
            profile wins, so those stay where they are.
          </span>
        </p>
      )}
      {result.sample.length > 0 && (
        <div className="border border-slate-200 rounded-md overflow-x-auto">
          <table className="w-full text-xs">
            <tbody>
              {result.sample.map((row) => (
                <tr key={row.id} className="border-b border-slate-100 last:border-0">
                  <td className="px-2 py-1 text-slate-500 whitespace-nowrap">
                    {(row.transaction_date || "").slice(0, 10)}
                  </td>
                  <td className="px-2 py-1 text-slate-700 max-w-[14rem] truncate">
                    {row.counterparty_name}
                  </td>
                  <td className="px-2 py-1 text-right text-slate-600 tabular-nums whitespace-nowrap">
                    {row.amount} {row.currency}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {result.claimed > result.sample.length && (
        <p className="text-xs text-slate-400">
          Showing {result.sample.length} of {result.claimed}.
        </p>
      )}
    </div>
  );
};

/**
 * A vendor's API key: set it here, never read it back.
 *
 * There is no endpoint that returns a stored secret, so this shows what it can
 * honestly show: which integration it is for, a fingerprint so you can tell one
 * key from another, when it was set, and whether the last fetch worked. The
 * fingerprint exists precisely so the screen never has to display the key to
 * answer "is this the one I just made".
 *
 * The scope hint sits next to the fields rather than in documentation, because
 * the moment to say "use a billing-reader role, not an ads token that can spend
 * money" is while somebody is creating the key.
 */
const CredentialSection = ({ profileId }) => {
  const [state, setState] = useState(null);
  const [catalogue, setCatalogue] = useState([]);
  const [adapter, setAdapter] = useState("");
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const [entering, setEntering] = useState(false);

  useEffect(() => {
    if (!profileId) {
      setState({ configured: false });
      return;
    }
    let live = true;
    Promise.all([
      apiService.getVendorCredential(profileId),
      apiService.getVendorAdapters(),
    ])
      .then(([cred, cat]) => {
        if (!live) return;
        setState(cred.data);
        setCatalogue(cat.data.adapters || []);
        setAdapter(cred.data.adapter || (cat.data.adapters || [])[0]?.name || "");
      })
      .catch(() => live && setState({ configured: false }));
    return () => {
      live = false;
    };
  }, [profileId]);

  const chosen = catalogue.find((a) => a.name === adapter);

  const save = async () => {
    setBusy(true);
    try {
      const res = await apiService.setVendorCredential(profileId, {
        adapter,
        // Optional fields are sent only when filled. An empty second factor
        // would otherwise be stored as one.
        secret: Object.fromEntries(
          [
            ...(chosen?.secret_fields || []),
            ...(chosen?.optional_secret_fields || []),
          ]
            .map((f) => [f, (fields[f] || "").trim()])
            .filter(([field, value]) => value || (chosen?.secret_fields || []).includes(field))
        ),
      });
      setState(res.data);
      setFields({});
      setEntering(false);
      toast.success("Key stored");
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not store this key");
    } finally {
      setBusy(false);
    }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await apiService.clearVendorCredential(profileId);
      setState({ configured: false });
      toast.success("Key removed");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not remove this key");
    } finally {
      setBusy(false);
    }
  };

  if (!profileId) {
    return (
      <p className="text-xs text-slate-500">
        Save this vendor first, then you can store an API key for it.
      </p>
    );
  }

  if (!state) return <p className="text-xs text-slate-400">Checking...</p>;

  return (
    <div className="space-y-3">
      {state.configured && !entering && (
        <div className="text-xs text-slate-600 space-y-1">
          <p className="flex items-center gap-1.5">
            <KeyRound className="w-3 h-3 text-emerald-600" />
            <span>
              {/* The label, not the machine name: "mongodb_atlas" is how the
                  code refers to it, not how a person does. */}
              Key set for{" "}
              <strong>
                {catalogue.find((a) => a.name === state.adapter)?.label || state.adapter}
              </strong>
              , fingerprint <span className="font-mono">{state.fingerprint}</span>
            </span>
          </p>
          <p className="text-slate-500">
            {state.last_fetch_error
              ? `Last fetch failed: ${state.last_fetch_error}`
              : state.last_fetch_at
              ? `Last fetched ${state.last_fetch_at.slice(0, 10)}`
              : "Not fetched yet"}
          </p>
          <div className="flex items-center gap-2 pt-1">
            <Button variant="outline" size="sm" className="h-7 text-xs"
              onClick={() => setEntering(true)} disabled={busy}>
              Replace
            </Button>
            <Button variant="ghost" size="sm" className="h-7 text-xs text-slate-500 hover:text-red-600"
              onClick={clear} disabled={busy}>
              Remove
            </Button>
          </div>
        </div>
      )}

      {(!state.configured || entering) && (
        <div className="space-y-2">
          <Select value={adapter} onValueChange={setAdapter}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Which API" />
            </SelectTrigger>
            <SelectContent>
              {catalogue.map((a) => (
                <SelectItem key={a.name} value={a.name}>
                  {a.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {chosen && (
            <>
              <p className="text-xs text-amber-700 flex items-start gap-1.5">
                <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                {chosen.scope_hint}
              </p>
              {[
                ...chosen.secret_fields.map((f) => [f, true]),
                ...(chosen.optional_secret_fields || []).map((f) => [f, false]),
              ].map(([field, required]) => (
                <Input
                  key={field}
                  // A username is not a secret, but masking the whole set keeps
                  // the shoulder-surfing story simple and costs nothing.
                  type="password"
                  autoComplete="off"
                  value={fields[field] || ""}
                  onChange={(e) => setFields((f) => ({ ...f, [field]: e.target.value }))}
                  placeholder={required ? field : `${field} (optional)`}
                  className="h-8 text-xs font-mono"
                  aria-label={field}
                />
              ))}
              <p className="text-xs text-slate-500">
                Stored encrypted. It is never shown again and never leaves the backend.
                {chosen.can_fetch === false
                  ? " Nothing fetches with it yet; it is held until something does."
                  : ""}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                  onClick={save}
                  disabled={busy || chosen.secret_fields.some((f) => !(fields[f] || "").trim())}>
                  {busy && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
                  Store
                </Button>
                {entering && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs"
                    onClick={() => { setEntering(false); setFields({}); }} disabled={busy}>
                    Cancel
                  </Button>
                )}
              </div>
            </>
          )}
          {catalogue.length === 0 && (
            <p className="text-xs text-slate-500">
              No vendor API is wired up in this build yet, so there is nothing to store a key for.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const ProfileDialog = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY_PROFILE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ ...EMPTY_PROFILE, ...(initial || {}) });
  }, [open, initial]);

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const criteria = useMemo(
    () => ({
      match_patterns: form.match_patterns,
      match_card_last_four: form.match_card_last_four,
      match_category_ids: form.match_category_ids,
      match_amount_min: form.match_amount_min,
      match_amount_max: form.match_amount_max,
    }),
    [form]
  );

  const noDocument = form.document_kind === "none";

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        no_document_reason: noDocument ? form.no_document_reason : null,
        portal_url: form.portal_url || null,
        portal_hint: form.portal_hint || null,
        account_ref: form.account_ref || null,
        notes: form.notes || null,
      };
      delete payload.id;
      delete payload.created_at;
      delete payload.updated_at;
      const res = await apiService.saveVendorProfile(payload, initial?.id);
      toast.success(initial?.id ? "Profile updated" : "Profile created");
      onSaved(res.data);
      onOpenChange(false);
    } catch (error) {
      // The backend names the field and the rule; repeating that beats a
      // generic failure the person has to guess at.
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save this profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{initial?.id ? "Edit vendor" : "New vendor"}</DialogTitle>
          <DialogDescription>
            Where this supplier's documents come from, and which charges belong to it.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="vendor-name">Name</Label>
            <Input
              id="vendor-name"
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="Google Cloud (large account)"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="vendor-patterns">Merchant patterns</Label>
            <Input
              id="vendor-patterns"
              value={form.match_patterns.join(", ")}
              onChange={(e) =>
                set(
                  "match_patterns",
                  e.target.value.split(",").map((p) => p.trim()).filter(Boolean)
                )
              }
              placeholder="Anthropic, Claude"
              className="font-mono text-xs"
            />
            <p className="text-xs text-slate-500">
              Matched against the counterparty and the description, ignoring case. Any one of
              them matching is enough. Separate with commas.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="vendor-cards">Only these cards</Label>
              <Input
                id="vendor-cards"
                value={form.match_card_last_four.join(", ")}
                onChange={(e) =>
                  set(
                    "match_card_last_four",
                    e.target.value.split(",").map((c) => c.trim()).filter(Boolean)
                  )
                }
                placeholder="8422"
                className="font-mono text-xs"
              />
              <p className="text-xs text-slate-500">
                Last four digits. How two accounts on one merchant are told apart.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label>Only this amount range</Label>
              <div className="flex items-center gap-2">
                <Input
                  aria-label="Minimum amount"
                  type="number"
                  value={form.match_amount_min ?? ""}
                  onChange={(e) =>
                    set("match_amount_min", e.target.value === "" ? null : Number(e.target.value))
                  }
                  placeholder="min"
                  className="tabular-nums"
                />
                <Input
                  aria-label="Maximum amount"
                  type="number"
                  value={form.match_amount_max ?? ""}
                  onChange={(e) =>
                    set("match_amount_max", e.target.value === "" ? null : Number(e.target.value))
                  }
                  placeholder="max"
                  className="tabular-nums"
                />
              </div>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2">
            <p className="text-xs font-medium text-slate-600 uppercase tracking-wider">
              What this rule claims
            </p>
            <RulePreview criteria={criteria} profileId={initial?.id} />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>What they send</Label>
              <Select value={form.document_kind} onValueChange={(v) => set("document_kind", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_KINDS.map((k) => (
                    <SelectItem key={k.value} value={k.value}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>How often</Label>
              <Select value={form.cadence} onValueChange={(v) => set("cadence", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CADENCES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {noDocument ? (
            <div className="space-y-1.5">
              <Label htmlFor="vendor-reason">Why there is never a document</Label>
              <Textarea
                id="vendor-reason"
                value={form.no_document_reason || ""}
                onChange={(e) => set("no_document_reason", e.target.value)}
                placeholder="Bank-initiated or internal. The statement is the record."
                rows={2}
              />
              <p className="text-xs text-slate-500">
                These charges leave the missing-receipt count, so the reason has to be
                findable later.
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label htmlFor="vendor-portal">Portal</Label>
                <Input
                  id="vendor-portal"
                  value={form.portal_url || ""}
                  onChange={(e) => set("portal_url", e.target.value)}
                  placeholder="https://console.anthropic.com/settings/billing"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="vendor-hint">How to get in</Label>
                  <Input
                    id="vendor-hint"
                    value={form.portal_hint || ""}
                    onChange={(e) => set("portal_hint", e.target.value)}
                    placeholder="Google profile /u/3/"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="vendor-account">Account reference</Label>
                  <Input
                    id="vendor-account"
                    value={form.account_ref || ""}
                    onChange={(e) => set("account_ref", e.target.value)}
                    placeholder="Billing ID 4261-8432-2033"
                    className="font-mono text-xs"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Which amount identifies their document</Label>
                <Select value={form.match_on} onValueChange={(v) => set("match_on", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MATCH_ON.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {!noDocument && (
            <div className="space-y-1.5 border-t border-slate-200 pt-4">
              <Label>Credentials</Label>
              <p className="text-xs text-slate-500">
                Optional. An API key where the vendor has one, otherwise a portal login held
                for later. Either way it is encrypted and never shown again.
              </p>
              <CredentialSection profileId={initial?.id} />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="vendor-notes">Notes</Label>
            <Textarea
              id="vendor-notes"
              value={form.notes || ""}
              onChange={(e) => set("notes", e.target.value)}
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={saving || !form.name.trim()}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving && <Loader2 className="w-3 h-3 mr-1.5 animate-spin" />}
            {initial?.id ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const VendorsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin: canDelete } = useAuth();
  const [profiles, setProfiles] = useState([]);
  const [credentials, setCredentials] = useState({});
  const [adapterCatalogue, setAdapters] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiService.getVendorProfiles();
      const loaded = res.data.profiles || [];
      setProfiles(loaded);
      // Which vendors hold a key, so the row can say so. One call per vendor
      // because there is no endpoint that answers it in bulk, and a failure
      // here must not stop the register rendering: the column just goes quiet.
      const states = await Promise.all(
        loaded.map((p) =>
          apiService
            .getVendorCredential(p.id)
            .then((r) => [p.id, r.data])
            .catch(() => [p.id, null])
        )
      );
      setCredentials(Object.fromEntries(states));
      // The catalogue, so a row can tell a key that fetches from a login that
      // is only being held. Derived from the adapter rather than from its
      // name, which would put the same fact in two places.
      try {
        const cat = await apiService.getVendorAdapters();
        setAdapters(
          Object.fromEntries((cat.data.adapters || []).map((a) => [a.name, a]))
        );
      } catch (error) {
        setAdapters({});
      }
    } catch (error) {
      toast.error("Could not load the register");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Arriving from a transaction: either open the profile it already has, or
  // start a new one with its merchant string as the draft pattern. The state is
  // cleared so a refresh does not reopen the dialog.
  // Handled once per arrival, tracked by identity rather than by a cancel
  // flag. The flag was the bug: clearing the navigation state re-runs this
  // effect, whose cleanup then cancelled the fetch that was about to open the
  // dialog, so "Record it" navigated here and did nothing. Found by clicking
  // it, not by reading it.
  const handledState = useRef(null);

  useEffect(() => {
    const incoming = location.state;
    if (!incoming || loading || handledState.current === incoming) return;
    handledState.current = incoming;

    const open = async () => {
      if (incoming.openProfileId) {
        const found = profiles.find((p) => p.id === incoming.openProfileId);
        if (found) {
          setEditing(found);
          setDialogOpen(true);
        }
        return;
      }
      const tx = incoming.fromTransaction;
      if (!tx) return;
      // The draft pattern comes from the backend, which owns matching and can
      // be tested on it. A failed suggestion is not a failed dialog: the person
      // can type the pattern, and the preview tells them whether it is right
      // either way.
      let pattern = "";
      try {
        const res = await apiService.suggestVendorPattern(tx.counterparty_name || "");
        pattern = res.data.pattern || "";
      } catch (error) {
        pattern = "";
      }
      setEditing({
        ...EMPTY_PROFILE,
        name: tx.counterparty_name || "",
        match_patterns: [pattern].filter(Boolean),
      });
      setDialogOpen(true);
    };

    open();
    // Cleared so a refresh does not reopen the dialog. Safe to do straight
    // away: the open above no longer depends on this state surviving.
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, loading, profiles]);

  const saveField = async (profile, field, value) => {
    const payload = { ...profile, [field]: value || null };
    delete payload.id;
    delete payload.created_at;
    delete payload.updated_at;
    const res = await apiService.saveVendorProfile(payload, profile.id);
    setProfiles((current) => current.map((p) => (p.id === profile.id ? res.data : p)));
    toast.success("Saved");
  };

  const remove = async (profile) => {
    try {
      await apiService.deleteVendorProfile(profile.id);
      setProfiles((current) => current.filter((p) => p.id !== profile.id));
      toast.success(`${profile.name} removed`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Could not remove this profile");
    }
  };

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return profiles;
    return profiles.filter(
      (p) =>
        p.name.toLowerCase().includes(needle) ||
        (p.account_ref || "").toLowerCase().includes(needle) ||
        (p.match_patterns || []).some((m) => m.toLowerCase().includes(needle))
    );
  }, [profiles, search]);

  const missingPortal = profiles.filter(
    (p) => p.document_kind !== "none" && !p.portal_url
  ).length;

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">Vendors</h1>
          <p className="text-sm text-slate-500 mt-1">
            Where each supplier's invoices come from, and which charges belong to it.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setDialogOpen(true);
          }}
          className="bg-emerald-600 hover:bg-emerald-700 whitespace-nowrap"
        >
          <Plus className="w-4 h-4 mr-1.5" />
          New vendor
        </Button>
      </div>

      {missingPortal > 0 && (
        <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {missingPortal} {missingPortal === 1 ? "vendor has" : "vendors have"} no portal
            recorded yet, so there is nowhere to send somebody chasing the invoice.
          </span>
        </div>
      )}

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, pattern or account reference"
          className="pl-9"
        />
      </div>

      {loading ? (
        <p className="text-sm text-slate-500 py-8 text-center">Loading the register...</p>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-slate-200 rounded-lg">
          <Store className="w-6 h-6 text-slate-300 mx-auto" />
          <p className="text-sm text-slate-600 mt-3">
            {profiles.length === 0
              ? "No vendors recorded yet."
              : "No vendor matches that search."}
          </p>
          <p className="text-xs text-slate-500 mt-1">
            {profiles.length === 0
              ? "A vendor profile is what tells a transaction where its invoice comes from."
              : "Try a shorter search."}
          </p>
        </div>
      ) : (
        <div className="border border-slate-200 rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left">
                <th className="px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Vendor</th>
                <th className="px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Sends</th>
                <th className="px-3 py-2 font-medium text-slate-600 whitespace-nowrap">Portal</th>
                <th className="px-3 py-2 font-medium text-slate-600 whitespace-nowrap">
                  Account reference
                </th>
                <th className="px-3 py-2 font-medium text-slate-600 whitespace-nowrap">
                  Credentials
                </th>
                <th className="px-3 py-2 w-20" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((profile) => {
                const noDocument = profile.document_kind === "none";
                return (
                  <tr key={profile.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2 align-top">
                      <button
                        type="button"
                        onClick={() => {
                          setEditing(profile);
                          setDialogOpen(true);
                        }}
                        className="font-medium text-slate-800 hover:text-emerald-700 text-left"
                      >
                        {profile.name}
                      </button>
                      {(profile.match_patterns || []).length > 0 && (
                        <div className="font-mono text-xs text-slate-400 mt-0.5 truncate max-w-[16rem]">
                          {profile.match_patterns.join(", ")}
                        </div>
                      )}
                      {noDocument && profile.no_document_reason && (
                        // Under the name, because it explains the vendor. It
                        // first sat in the Account reference column, where it
                        // said nothing true about that heading and stretched
                        // the row to four lines.
                        <div
                          className="text-xs text-slate-500 mt-1 max-w-[22rem] line-clamp-2"
                          title={profile.no_document_reason}
                        >
                          {profile.no_document_reason}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 align-top whitespace-nowrap">
                      {noDocument ? (
                        <Badge variant="outline" className="text-slate-500 border-slate-300">
                          <X className="w-3 h-3 mr-1" />
                          No document
                        </Badge>
                      ) : (
                        <span className="text-slate-600 text-xs">
                          {DOCUMENT_KINDS.find((k) => k.value === profile.document_kind)?.label ||
                            profile.document_kind}
                        </span>
                      )}
                    </td>
                    {noDocument ? (
                      // One cell across all three: none of those headings has
                      // anything to say about a charge whose document does not
                      // exist, and empty cells read as missing data.
                      <td className="px-3 py-2 align-top text-xs text-slate-400" colSpan={3}>
                        Nothing to fetch
                      </td>
                    ) : (
                      <>
                        <td className="px-3 py-2 align-top max-w-[18rem]">
                          <div className="flex items-center gap-2 min-w-0">
                            <InlineText
                              value={profile.portal_url}
                              placeholder="Add a portal"
                              label={`portal for ${profile.name}`}
                              onSave={(v) => saveField(profile, "portal_url", v)}
                            />
                            {profile.portal_url && (
                              <a
                                href={profile.portal_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Open the portal for ${profile.name}`}
                                className="text-slate-400 hover:text-emerald-700 flex-shrink-0"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 align-top max-w-[14rem]">
                          <InlineText
                            value={profile.account_ref}
                            placeholder="Add a reference"
                            label={`account reference for ${profile.name}`}
                            mono
                            onSave={(v) => saveField(profile, "account_ref", v)}
                          />
                        </td>
                        {/* Jelle asked where to put a key, which was the
                            answer: nothing on the row said one could be set,
                            or whether one was, so the only way in was guessing
                            that the name is clickable. */}
                        <td className="px-3 py-2 align-top whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(profile);
                              setDialogOpen(true);
                            }}
                            className="inline-flex items-center gap-1.5 text-xs"
                          >
                            {credentials[profile.id]?.configured ? (
                              <>
                                <KeyRound
                                  className={`w-3 h-3 ${
                                    credentials[profile.id]?.last_fetch_error
                                      ? "text-amber-600"
                                      : adapterCatalogue[credentials[profile.id]?.adapter]
                                          ?.can_fetch === false
                                      ? "text-slate-400"
                                      : "text-emerald-600"
                                  }`}
                                />
                                <span className="text-slate-600">
                                  {credentials[profile.id]?.last_fetch_error
                                    ? "Set, last fetch failed"
                                    : adapterCatalogue[credentials[profile.id]?.adapter]
                                        ?.can_fetch === false
                                    ? "Stored, not used yet"
                                    : "Set"}
                                </span>
                              </>
                            ) : (
                              <span className="text-slate-400 italic hover:text-slate-600">
                                Add access
                              </span>
                            )}
                          </button>
                        </td>
                      </>
                    )}
                    <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                      {/* Deleting reference data is admin-only on the API, so a
                          manager would get a 403 from a button that looked
                          available. Editing is theirs; removing is not. */}
                      {canDelete && (
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-slate-400 hover:text-red-600"
                          aria-label={`Remove ${profile.name}`}
                          onClick={() => remove(profile)}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ProfileDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={load}
      />
    </div>
  );
};

export default VendorsPage;
